import { createElement as h, useEffect, useRef, useState } from "react";
import type { CameraPose, CameraPreset, ControlsParams, SceneState, Vec3 } from "../../../shared/api-contract";
import type { BindingCtx, DomainRead } from "../../../state/editor/types";
import { sc, uiBinding } from "../inspector/bindings";
import type { Binding, SectionDef } from "../inspector/schema";
import { CompactNumber, MiniBtn } from "../inspector/rows";
import { RendererControl } from "./custom/RendererControl";
import { ControlsControl } from "./custom/ControlsControl";

/**
 * Viewer tab — Renderer / Camera / Drop Options. Transcribed from the legacy
 * `CameraMenu.tsx` and `ModelsMenu.tsx` (the v12 "View" buttons only). Renderer
 * params are engine wave 1, and auto-center/auto-scale are wave 8, so those land
 * as NOTE placeholders rather than wiring RPCs that don't exist yet.
 *
 * Components are authored with React.createElement so this stays a `.ts` module
 * (the sections registry imports it extensionless).
 */

const PRESETS: { label: string; id: CameraPreset }[] = [
  { label: "Front", id: "front" },
  { label: "Back", id: "back" },
  { label: "Left", id: "left" },
  { label: "Right", id: "right" },
  { label: "Top", id: "top" },
  { label: "3/4", id: "threeQuarter" },
];

/**
 * Inline Binding<number> for one component of the polar (vertical orbit) limits.
 * Reads `scene.polarLimits[i]`, optimistically patches the pair, and calls
 * `setPolarLimits(min, max)` with the other component preserved.
 */
function polarLimit(i: 0 | 1): Binding<number> {
  return {
    domain: "scene",
    get: (read: DomainRead) => (read("scene") as SceneState).polarLimits[i],
    set: (v: number, ctx: BindingCtx) => {
      const cur = (ctx.read("scene") as SceneState).polarLimits;
      const next: [number, number] = i === 0 ? [v, cur[1]] : [cur[0], v];
      return {
        patch: { polarLimits: next } as Partial<SceneState>,
        call: (api) => Promise.resolve(api.setPolarLimits(next[0], next[1])),
      };
    },
  };
}

/** Module-scoped saved pose, shared across remounts of the Save/Restore row. */
const savedPose: { current: CameraPose | null } = { current: null };

/**
 * Camera Position / Target: polls getCameraPosition + getCameraTarget every
 * 500 ms and shows editable X/Y/Z with an Apply button. Ported from CameraMenu.
 */
function CameraPoseRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [pos, setPos] = useState<Vec3>({ x: 0, y: 0, z: 0 });
  const [tgt, setTgt] = useState<Vec3>({ x: 0, y: 0, z: 0 });
  const editing = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // `editing` latches on the first keystroke and used to clear ONLY inside
  // Apply, so abandoning an edit (clicking away, orbiting the model) froze the
  // poll — and with it the Position/Target readout — on values the camera never
  // had, for the rest of the session. A pointer-down outside this row is the
  // abandon signal; presses inside it (including Apply itself) keep the latch,
  // so a typed-then-applied value can't be clobbered by a tick in between.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!editing.current) return;
      if (rootRef.current?.contains(e.target as Node)) return;
      editing.current = false;
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (editing.current) return;
      const [p, t] = await Promise.all([r.getCameraPosition(), r.getCameraTarget()]);
      if (!alive) return;
      setPos(p);
      setTgt(t);
    };
    void tick();
    const id = window.setInterval(() => void tick(), 500);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [r]);

  const setPosComp = (k: keyof Vec3, v: number) => {
    editing.current = true;
    setPos((q) => ({ ...q, [k]: v }));
  };
  const setTgtComp = (k: keyof Vec3, v: number) => {
    editing.current = true;
    setTgt((q) => ({ ...q, [k]: v }));
  };

  const xyz = (val: Vec3, on: (k: keyof Vec3, v: number) => void) =>
    (["x", "y", "z"] as (keyof Vec3)[]).map((k) =>
      h(CompactNumber, {
        key: k,
        value: val[k],
        step: 0.1,
        width: 52,
        onChange: (v: number) => on(k, v),
      }),
    );

  return h(
    "div",
    { className: "flex flex-col gap-1.5 px-3 py-1.5", ref: rootRef },
    h(
      "div",
      { className: "flex items-center gap-1.5" },
      h("span", { className: "w-10 shrink-0 text-[12px] text-ed-text-2" }, "Position"),
      ...xyz(pos, setPosComp),
    ),
    h(
      "div",
      { className: "flex items-center gap-1.5" },
      h("span", { className: "w-10 shrink-0 text-[12px] text-ed-text-2" }, "Target"),
      ...xyz(tgt, setTgtComp),
    ),
    h(
      "div",
      { className: "flex justify-end" },
      h(MiniBtn, {
        primary: true,
        onClick: () => {
          void r.setCameraPosition(pos.x, pos.y, pos.z);
          void r.setCameraTarget(tgt.x, tgt.y, tgt.z);
          editing.current = false;
        },
        children: "Apply",
      }),
    ),
  );
}

/**
 * Camera position clamp AABB (`ControlsParams.clampMin/clampMax`, iJewel
 * Camera › Controls). Live: CameraController._applyPositionClamp clamps both
 * camera and target on every control change. Queried directly rather than
 * through `ControlsControl` since that component only covers the
 * OrbitControls-native fields.
 */
function ClampRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [p, setP] = useState<ControlsParams | null>(null);
  useEffect(() => {
    r.getControlsParams().then(setP).catch(() => {});
  }, [r]);
  if (!p) return null;
  const set = (patch: Partial<ControlsParams>) => r.setControlsParams(patch).then(setP);
  const vec = (v: Vec3, key: "clampMin" | "clampMax") =>
    (["x", "y", "z"] as (keyof Vec3)[]).map((k) =>
      h(CompactNumber, {
        key: k,
        value: v[k],
        step: 1,
        width: 60,
        onChange: (nv: number) => void set({ [key]: { ...v, [k]: nv } } as Partial<ControlsParams>),
      }),
    );
  return h(
    "div",
    { className: "flex flex-col gap-1.5 px-3 py-1.5" },
    h(
      "div",
      { className: "flex items-center gap-1.5" },
      h("span", { className: "w-16 shrink-0 text-[12px] text-ed-text-2" }, "Clamp Min"),
      ...vec(p.clampMin, "clampMin"),
    ),
    h(
      "div",
      { className: "flex items-center gap-1.5" },
      h("span", { className: "w-16 shrink-0 text-[12px] text-ed-text-2" }, "Clamp Max"),
      ...vec(p.clampMax, "clampMax"),
    ),
  );
}

export const viewerSections: SectionDef[] = [
  {
    id: "renderer",
    title: "Renderer",
    rows: [
      {
        id: "renderer-control",
        kind: "custom",
        label: "",
        component: RendererControl,
      },
    ],
  },
  {
    id: "camera",
    title: "Camera",
    rows: [
      {
        id: "fov",
        kind: "slider",
        label: "Field Of View",
        min: 1,
        max: 180,
        step: 1,
        binding: sc(
          (s) => s.fov,
          (v) => ({ fov: v }),
          (r, v) => r.setFov(v),
        ),
      },
      {
        id: "fit",
        kind: "button",
        label: "",
        buttons: [
          { label: "Fit to View", icon: "fit_screen", run: (ctx) => ctx.ring.resetView() },
          { label: "Zoom in", icon: "zoom_in", run: (ctx) => ctx.ring.zoomCamera(0.85) },
          { label: "Zoom out", icon: "zoom_out", run: (ctx) => ctx.ring.zoomCamera(1.18) },
        ],
      },
      {
        id: "presets",
        kind: "button",
        label: "Preset Views",
        buttons: PRESETS.map((p) => ({
          label: p.label,
          run: (ctx) => ctx.ring.setCameraPreset(p.id),
        })),
      },
      {
        // A SECOND row, deliberately, rather than extending the six above:
        // those labels feed verify:parity's coverage metric and are the
        // reference's own. These three are composed camera maths, not presets,
        // and existed only in the Canvas chrome.
        id: "poseExtras",
        kind: "button",
        label: "Composed Views",
        buttons: [
          {
            label: "Macro",
            run: async (ctx) => {
              await ctx.ring.setCameraPreset("threeQuarter");
              ctx.ring.zoomCamera(0.6);
            },
          },
          {
            label: "Low angle",
            run: async (ctx) => {
              // Mirror the camera below the target so it looks UP at the piece.
              const [p, t] = await Promise.all([ctx.ring.getCameraPosition(), ctx.ring.getCameraTarget()]);
              ctx.ring.setCameraPosition(p.x, t.y - Math.abs(p.y - t.y), p.z);
            },
          },
          {
            label: "Tilt",
            run: async (ctx) => {
              // ~25° of azimuth for a canted three-quarter.
              const [p, t] = await Promise.all([ctx.ring.getCameraPosition(), ctx.ring.getCameraTarget()]);
              const dx = p.x - t.x;
              const dz = p.z - t.z;
              const a = 0.44;
              ctx.ring.setCameraPosition(
                t.x + dx * Math.cos(a) - dz * Math.sin(a),
                p.y,
                t.z + dx * Math.sin(a) + dz * Math.cos(a),
              );
            },
          },
        ],
      },
      // The unit is in the label on purpose: the same limit is also editable as
      // "Min/Max Polar Angle" in the (controls) block below, where the reference
      // schema puts it in RADIANS. Unlabelled, one row read 180 and the other
      // 3.1416 for the same setting. Both rows now write `scene.polarLimits`
      // (degrees) through setPolarLimits, so they stay in step.
      {
        id: "orbit-vmin",
        kind: "slider",
        label: "Orbit V Min (°)",
        hint: "Vertical orbit limit in degrees (0 = straight above the model).",
        min: 0,
        max: 180,
        step: 1,
        binding: polarLimit(0),
      },
      {
        id: "orbit-vmax",
        kind: "slider",
        label: "Orbit V Max (°)",
        hint: "Vertical orbit limit in degrees (180 = straight below the model).",
        min: 0,
        max: 180,
        step: 1,
        binding: polarLimit(1),
      },
      // NOTE: the legacy "Auto Rotate" checkbox + speed slider that used to sit
      // here is gone. It kept `on`/`speed` in local useState that nothing ever
      // seeded from the viewer, so it rendered "off" over an already-rotating
      // scene (a project/vjson can enable auto-rotate at load) and its speed
      // slider silently sent nothing whenever that stale flag was false. It also
      // duplicated the "Auto Rotate" / "Auto Rotate Speed" rows ControlsControl
      // renders a few rows below — two independent mirrors of one OrbitControls
      // field, so toggling either left the other showing the opposite value.
      // ControlsControl's pair is the reference-schema one, it seeds from
      // getControlsParams and re-reads the params returned by every write, and
      // setControlsParams({autoRotate,autoRotateSpeed}) hits exactly the same
      // engine fields (+ requestRender) as setAutoRotate did.
      {
        id: "save-restore",
        kind: "button",
        label: "Saved View",
        buttons: [
          {
            label: "Save",
            icon: "bookmark",
            run: async (ctx) => {
              savedPose.current = await ctx.ring.saveCameraPose();
            },
          },
          {
            label: "Restore",
            icon: "restore",
            run: (ctx) => {
              if (savedPose.current) ctx.ring.restoreCameraPose(savedPose.current);
            },
          },
        ],
      },
      {
        id: "camera-pose",
        kind: "custom",
        label: "Position / Target",
        domains: ["scene"],
        component: CameraPoseRow,
      },
      // (controls) — the full OrbitControls + camera parameter set. iJewel's
      // checklist captures these as part of the Camera section rather than a
      // standalone panel, so they're folded in here instead of split out.
      { id: "controlsControl", kind: "custom", label: "", domains: ["scene"], component: ControlsControl },
      { id: "controlsClamp", kind: "custom", label: "Clamp AABB", component: ClampRow },
    ],
  },
  {
    id: "drop-options",
    title: "Drop Options",
    defaultCollapsed: true,
    rows: [
      {
        id: "drop-note",
        kind: "note",
        label: "",
        text: "Applied to dropped / opened model files (OBJ · FBX · STL · glTF · 3DM).",
      },
      {
        id: "autoCenter",
        kind: "checkbox",
        label: "Auto Center",
        binding: uiBinding(
          (ui) => !!(ui.dropOptions as { autoCenter?: boolean })?.autoCenter,
          (v, ui) => ({ dropOptions: { ...(ui.dropOptions as object), autoCenter: v } }),
        ),
      },
      {
        id: "autoScale",
        kind: "checkbox",
        label: "Auto Scale",
        binding: uiBinding(
          (ui) => !!(ui.dropOptions as { autoScale?: boolean })?.autoScale,
          (v, ui) => ({ dropOptions: { ...(ui.dropOptions as object), autoScale: v } }),
        ),
      },
      {
        id: "autoScaleRadius",
        kind: "slider",
        label: "Target Radius",
        min: 0.5,
        max: 8,
        step: 0.1,
        binding: uiBinding(
          (ui) => (ui.dropOptions as { autoScaleRadius?: number })?.autoScaleRadius ?? 2,
          (v, ui) => ({ dropOptions: { ...(ui.dropOptions as object), autoScaleRadius: v } }),
        ),
      },
      {
        id: "auto-place",
        kind: "button",
        label: "",
        buttons: [
          { label: "Auto Center", icon: "center_focus_strong", run: (ctx) => ctx.ring.autoCenter() },
          { label: "Auto Fit", icon: "fit_screen", run: (ctx) => ctx.ring.autoFit() },
        ],
      },
    ],
  },
];
